import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { DirtyInvalidErrorStateMatcher } from '@examdojo/angular/forms';
import { connectState } from '@examdojo/angular/util';
import { ErrorMessages, FormFieldLabelDirective, selectFirstErrorMessage } from '@examdojo/core/form';
import { ComponentSize, DEFAULT_COMPONENT_SIZE } from '@examdojo/core/model';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { BehaviorSubject, filter, map, startWith, switchMap, tap } from 'rxjs';

const MULTI_LINE_TEXT_IMPORTS: Component['imports'] = [
  ReactiveFormsModule,
  FormFieldLabelDirective,
  NgIf,
  NgTemplateOutlet,
  NgClass,
];

/**
 * A multi-line text component that solves some of the textarea auto-resize issues.
 */
@Component({
  selector: 'dojo-multi-line-text',
  templateUrl: './multi-line-text.component.html',
  styleUrls: ['./multi-line-text.component.scss'],
  imports: [MULTI_LINE_TEXT_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class MultiLineTextComponent {
  constructor() {
    afterNextRender(() => {
      this.initialized$$.next(true);
    });

    this.handleInitialFocus().pipe(takeUntilDestroyed()).subscribe();
    this.syncFormControlValue().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly initialized$$ = new BehaviorSubject(false);
  private readonly multiLineTextDiv = viewChild<ElementRef<HTMLDivElement>>('multiLineTextDiv');
  private _isSettingValue = false;

  readonly formCtrl = input.required<FormControl<string | null | undefined>>();
  readonly formCtrlValue$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) => formCtrl.valueChanges.pipe(startWith(formCtrl.value))),
  );

  readonly label = input<string | TemplateRef<unknown> | null>();

  readonly hideErrors = input(false);
  readonly errorMessages = input<ErrorMessages>();
  readonly placeholder = input<string>();
  readonly hasInitialFocus = input<boolean>();
  readonly compact = input<boolean | null>();
  readonly size = input<ComponentSize>(DEFAULT_COMPONENT_SIZE);
  readonly minHeight = input<string>();
  readonly maxHeight = input<string>();

  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly change = output<Event>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly blur = output<FocusEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly focus = output<FocusEvent>();

  readonly errorStateMatcher = input(new DirtyInvalidErrorStateMatcher());

  readonly labelTemplate = computed(() => {
    const label = this.label();
    if (label instanceof TemplateRef) {
      return label;
    }
    return null;
  });

  readonly state = connectState({
    errorMessage: this.initialized$$.pipe(
      filter(Boolean),
      switchMap(() => selectFirstErrorMessage(this.formCtrl(), this.errorMessages())),
    ),
    hasError: this.initialized$$.pipe(
      filter(Boolean),
      switchMap(() =>
        this.formCtrl().statusChanges.pipe(map(() => this.errorStateMatcher().isErrorState(this.formCtrl()))),
      ),
    ),
  });

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const target = event.target as HTMLDivElement;
    const value = target.innerText;

    this._isSettingValue = true;

    this.formCtrl().setValue(value);
    this.formCtrl().markAsDirty();

    this._isSettingValue = false;
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent) {
    this.formCtrl().markAsTouched();

    this.blur.emit(event);
  }

  @HostListener('focus', ['$event'])
  onFocus(event: FocusEvent) {
    this.focus.emit(event);
  }

  focusElement() {
    this.multiLineTextDiv()?.nativeElement.focus();
  }

  private handleInitialFocus() {
    return this.initialized$$.pipe(
      filter(Boolean),
      filter(() => !!this.hasInitialFocus()),
      tap(() => this.focusElement()),
    );
  }

  private syncFormControlValue() {
    return this.initialized$$.pipe(
      filter(Boolean),
      switchMap(() => this.formCtrlValue$),
      filter(isNotNullish),
      filter(() => !this._isSettingValue),
      tap((value) => {
        const div = this.multiLineTextDiv()?.nativeElement;
        if (div && div.innerText !== value) {
          div.innerText = value;
        }
      }),
    );
  }
}
